"use client";

import React, { useEffect, useRef, useState } from "react";
import { Handshake, Server, ExternalLink, Scale, Briefcase, ShieldCheck } from "lucide-react";
import { Card, CardHeader, CardTitle } from "@/components/ui/card";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const industries = [{
  id: 1,
  name: "Trusted Partners",
  icon: Handshake,
  description: "We may work with trusted partners where needed to support business operations and service delivery."
}, {
  id: 2,
  name: "Service Providers",
  icon: Server,
  description: "Selected providers may support hosting, analytics, communication, or technical operations under appropriate safeguards."
}, {
  id: 3,
  name: "Third-Party Tools",
  icon: ExternalLink,
  description: "Some website functions may rely on external tools, links, or platforms that operate under their own terms."
}, {
  id: 4,
  name: "Legal Requirements",
  icon: Scale,
  description: "Information may be disclosed where required by applicable law, regulation, or lawful authority."
}, {
  id: 5,
  name: "Business Operations",
  icon: Briefcase,
  description: "Limited information may be used to manage inquiries, maintain records, and support client communication."
}, {
  id: 6,
  name: "Protection of Rights",
  icon: ShieldCheck,
  description: "Data may be used where necessary to protect FETC, users, systems, or legitimate business interests."
}];
export default function DataSharing() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="252386" id="data-sharing" className="bg-[#0B1220] py-14 md:py-18 lg:py-20 min-h-screen flex items-center scroll-mt-[120px] snap-start overflow-x-hidden">
      <div ref={ref} className={`max-w-7xl mx-auto px-6 w-full transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold font-default text-[#FFFFFF] mb-6 tracking-tight">
            Data Sharing & Transparency
          </h2>
          <div className="w-16 h-1 bg-[#0F62FE] mx-auto rounded-md mb-8"></div>
          <p className="text-lg text-[#D6DCE8] max-w-3xl mx-auto font-sans leading-relaxed">
            Sharing only where required for business operations, trusted service providers, legal obligations, or protection of rights.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 w-full max-w-full overflow-hidden">
          {industries.map((industry, i) => {
          const Icon = industry.icon;
          return <Card key={industry.id} data-index={i} className="group relative overflow-hidden border-[rgba(15,98,254,0.15)] bg-[#142033] transition-all duration-500 hover:border-[#0F62FE]/40 hover:shadow-xl cursor-default rounded-lg">
                <div className="absolute top-0 left-0 w-1 h-0 bg-[#0F62FE] transition-all duration-300 group-hover:h-full" />
                <CardHeader className="p-6 flex flex-col items-center text-center">
                  <div className="mb-4 p-3 rounded-lg bg-[#0B1220] text-[#0F62FE] transition-colors duration-300 group-hover:bg-[#0F62FE] group-hover:text-white">
                    <Icon className="w-6 h-6" />
                  </div>
                  <CardTitle className="text-xl font-semibold font-default text-[#FFFFFF] mb-3">
                    {industry.name}
                  </CardTitle>
                  <p className="text-sm text-[#D6DCE8] leading-relaxed">
                    {industry.description}
                  </p>
                </CardHeader>
              </Card>;
        })}
        </div>
      </div>
    </section>;
}